import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

const HEADLINE_LINES = ["We build what", "the problem requires."];
const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
    const reduce = useReducedMotion();
    // Coordinates with the IntroReveal identity sequence: when the intro plays
    // this session, the headline holds until the overlay releases (~2.9s).
    const [base] = useState(() => {
        if (typeof window === "undefined") return 0.2;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return 0;
        return sessionStorage.getItem("thg-intro-v2") ? 0.2 : 2.9;
    });
    const { scrollY } = useScroll();
    const imgY = useTransform(scrollY, [0, 900], [0, 160]);

    return (
        <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
            <motion.div className="absolute inset-0" aria-hidden="true" style={reduce ? undefined : { y: imgY }}>
                <motion.img
                    src="https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?q=80&w=2000&auto=format&fit=crop"
                    alt=""
                    className="w-full h-[112%] object-cover"
                    loading="eager"
                    initial={reduce ? false : { scale: 1.08 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.4, ease: EASE, delay: base * 0.5 }}
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/70" />
            </motion.div>
            <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-24 md:pb-32">
                <motion.p
                    className="text-xs tracking-[0.25em] uppercase font-bold text-thg-secondary"
                    data-testid="hero-overline"
                    initial={reduce ? false : { opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: base }}
                >
                    Titan Haven Group LLC
                </motion.p>
                <h1
                    className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] font-medium text-white max-w-4xl"
                    aria-label="We build what the problem requires."
                >
                    {HEADLINE_LINES.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1" aria-hidden="true">
                            <motion.span
                                className="block"
                                initial={reduce ? false : { y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 0.9, ease: EASE, delay: base + 0.15 + i * 0.15 }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <motion.p
                    className="mt-8 text-base md:text-lg leading-relaxed text-thg-secondary max-w-2xl"
                    initial={reduce ? false : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: base + 0.5 }}
                >
                    Titan Haven Group builds and operates retail operations software and experimental
                    digital products — alongside consumer businesses, digital assets, and media/IP.
                </motion.p>
                <motion.p
                    className="mt-6 text-xs tracking-[0.2em] uppercase text-thg-tertiary"
                    data-testid="hero-method-line"
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.7, ease: EASE, delay: base + 0.65 }}
                >
                    Observe <span className="text-thg-red mx-1">&rarr;</span> Question{" "}
                    <span className="text-thg-red mx-1">&rarr;</span> Synthesize{" "}
                    <span className="text-thg-red mx-1">&rarr;</span> Act
                </motion.p>
                <motion.div
                    className="mt-10 flex flex-wrap gap-4"
                    initial={reduce ? false : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: base + 0.8 }}
                >
                    <a
                        href="#built"
                        data-testid="hero-cta-explore"
                        className="inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                    >
                        Explore Our Work <ArrowRight size={16} />
                    </a>
                    <Link
                        to="/company"
                        data-testid="hero-cta-company"
                        className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                    >
                        The Company
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};
